import { FunctionComponent, useContext } from 'react'
import useAddProductsToWishlist from '../../../core/hook/useAddProductsToWishlist'
import useRemoveProductsFromWishlist from '../../../core/hook/useRemoveProductsFromWishlist'
import useWishlist from '../../../core/hook/useWishlist'
import { UserContext } from '../../context/UserContext'
import { gtmCategory, gtmPush } from '../../utils/gtmHelpers'
import Icon from '../Icon/Icon'
import { Icons } from '../Icon/constants'
import { AlertNotificationContext } from '../../context/AlertNotificationContext'
import { useTranslation } from '../../context/ConfigContext'

type WishlistProps = {
  productSku: string
  className?: string
  product: Product
}

const Wishlist: FunctionComponent<WishlistProps> = ({ productSku, className, product }) => {
  const queryResultWishList = useWishlist()
  const addProductsToWishlist = useAddProductsToWishlist()
  const removeProductsFromWishlist = useRemoveProductsFromWishlist()
  const { addNotification } = useContext(AlertNotificationContext)
  const t = useTranslation()

  const items = queryResultWishList.data?.wishlist?.items

  const productOnWishlist = items?.find((item) => item?.product?.sku === productSku)

  const gtmEvent = () => {
    const storedValues = JSON.parse(window.localStorage.getItem('productValues') as any)
    const gtmCategoryObj = gtmCategory(product.gtm_categories)

    gtmPush({
      event: 'add_to_wishlist',
      ecommerce: {
        currency: product.price_range.minimum_price.regular_price.currency,
        value: product.price_range.minimum_price.regular_price.value,
        items: [
          {
            item_name: product.gtm_name,
            item_id: productSku,
            price: product.price_range.minimum_price.regular_price.value,
            item_brand: 'Messika',
            index: product?.configurable_options?.[0]?.position,
            item_list_id: storedValues ? storedValues.listId : 'produit',
            item_list_name: storedValues ? storedValues.listName : 'Produit',
            Item_variant: productSku,
            ...gtmCategoryObj,
          },
        ],
      },
    })
  }

  const handleWishlist = async () => {
    if (productOnWishlist?.id) {
      await removeProductsFromWishlist(productOnWishlist.id.toString())
      await queryResultWishList.refetch()
      return
    }
    gtmEvent()
    await addProductsToWishlist(productSku).then(() =>
      addNotification(
        t(
          '%1 has been added to your Wish List.',
          { variables: { 0: product?.name as string } },
          false,
        ),
      ),
    )
    await queryResultWishList.refetch()
  }

  return (
    <button type='button' className={`wishlist ${className}`} onClick={handleWishlist}>
      {productOnWishlist ? (
        <Icon iconName={Icons.WISHLIST_FULL} width={24} height={24} />
      ) : (
        <Icon iconName={Icons.WISHLIST} width={24} height={24} />
      )}
    </button>
  )
}

const WishlistContainer: FunctionComponent<WishlistProps> = ({
  productSku,
  className,
  product,
}) => {
  const { isLogged } = useContext(UserContext)

  if (!isLogged) {
    return null
  }

  return <Wishlist productSku={productSku} className={className} product={product} />
}

export default WishlistContainer
